import {
  Clock,
  ExternalLink,
  Instagram,
  Globe,
  MapPin,
  Phone,
  Star,
  ExternalLink as ExternalIcon,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines } from "@/components/anim";
import GlowCard from "@/components/GlowCard";
import BookingForm from "@/components/BookingForm";
import {
  PHONE_TEL,
  PHONE_DISPLAY,
  ADDRESS,
  MAPS_URL,
  MAPS_EMBED,
  INSTAGRAM_URL,
  GOOGLE_BUSINESS_URL,
  TRIPADVISOR_URL,
  WEBSITE_URL,
} from "@/site";

export default function Contatti() {
  const { t } = useLang();
  const c = t.contact;

  const cards = [
    {
      id: "address",
      icon: MapPin,
      title: c.cards.addressT,
      lines: [ADDRESS],
      href: MAPS_URL,
      hrefLabel: c.cards.directions,
    },
    {
      id: "phone",
      icon: Phone,
      title: c.cards.phoneT,
      lines: [PHONE_DISPLAY],
      href: PHONE_TEL,
      hrefLabel: c.cards.callUs,
    },
    {
      id: "hours",
      icon: Clock,
      title: c.cards.hoursT,
      lines: [c.cards.hoursDays, c.cards.hoursDetail],
    },
    {
      id: "rating",
      icon: Star,
      title: c.cards.ratingT,
      lines: [c.cards.ratingValue, c.cards.ratingSub],
      href: GOOGLE_BUSINESS_URL,
      hrefLabel: c.cards.googleProfile,
    },
  ];

  const socials = [
    { id: "instagram", icon: Instagram, label: c.socials.instagram, href: INSTAGRAM_URL },
    { id: "google", icon: Globe, label: c.socials.google, href: GOOGLE_BUSINESS_URL },
    { id: "tripadvisor", icon: Star, label: c.socials.tripadvisor, href: TRIPADVISOR_URL },
    { id: "website", icon: ExternalIcon, label: c.socials.website, href: WEBSITE_URL },
  ];

  return (
    <div>
      {/* PAGE HERO */}
      <section className="relative overflow-hidden pb-12 pt-36 sm:pt-44" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <p className="eyebrow">{c.eyebrow}</p>
          </Reveal>
          <h1 className="mt-6 font-display text-5xl italic leading-[0.98] text-white sm:text-7xl lg:text-8xl">
            <KineticLines
              mode="load"
              delay={0.15}
              lines={[c.titleA, <span className="text-gold">{c.titleB}</span>]}
            />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg">
              {c.intro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* INFO CARDS */}
      <section className="pb-10 pt-4 sm:pb-14" data-testid="contact-info-cards">
        <div className="mx-auto grid max-w-7xl gap-5 px-5 sm:grid-cols-2 sm:px-10 lg:grid-cols-4">
          {cards.map((card, i) => {
            const Icon = card.icon;
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <Icon size={22} strokeWidth={1.4} className="text-gold" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  {card.title}
                </h3>
                <div className="mt-3 flex-1">
                  {card.lines.map((line) => (
                    <p key={line} className="text-sm leading-relaxed text-white/90 sm:text-[15px]">
                      {line}
                    </p>
                  ))}
                </div>
                {card.href && (
                  <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-300 group-hover:text-gold">
                    {card.hrefLabel}
                    <ExternalLink size={11} />
                  </span>
                )}
              </>
            );
            const cls =
              "group flex min-h-[240px] flex-col border border-gold/10 bg-coal p-7";
            return (
              <Reveal key={card.id} delay={i * 0.1} y={44} className="h-full">
                <GlowCard
                  as={card.href ? "a" : "div"}
                  data-testid={`contact-card-${card.id}`}
                  {...(card.href
                    ? {
                        href: card.href,
                        target: card.href.startsWith("tel:") ? undefined : "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {})}
                  className={`${cls} h-full`}
                >
                  {inner}
                </GlowCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MAP — right beside the info cards */}
      <section className="py-10 sm:py-14" data-testid="contact-map-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <div className="mb-10 flex items-end justify-between gap-6">
              <h2 className="font-display text-3xl italic text-white sm:text-4xl">
                {c.mapTitle}
              </h2>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-link"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 transition-colors hover:text-gold"
              >
                {c.cards.directions}
                <ExternalLink size={12} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden border border-gold/15 bg-coal p-2 sm:p-3">
              <iframe
                title="Mappa — Ristorante Gunè, Via XXV Aprile 80, Castelnuovo del Garda"
                src={MAPS_EMBED}
                data-testid="contact-map-iframe"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="h-[360px] w-full border-0 opacity-95 brightness-[0.8] contrast-[1.05] grayscale-[45%] saturate-[0.8] sm:h-[480px]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section
        id="prenota"
        className="mt-10 border-y border-gold/10 bg-coal py-20 sm:py-28"
        data-testid="booking-section"
      >
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-10 lg:grid-cols-2 lg:gap-20">
          <div className="flex flex-col justify-center">
            <Reveal>
              <p className="eyebrow">{c.form.eyebrow}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl italic leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                {c.form.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-neutral-400 sm:text-base">
                {c.form.text}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href={PHONE_TEL}
                data-testid="booking-section-phone"
                className="link-gold mt-10 inline-block font-display text-3xl italic text-gold sm:text-4xl"
              >
                {PHONE_DISPLAY}
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-gold/15 bg-ink p-7 sm:p-10">
              <BookingForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOCIALS */}
      <section className="py-24" data-testid="contact-socials">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <p className="eyebrow text-center">{c.follow}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-link-${s.id}`}
                    className="inline-flex items-center gap-3 border border-gold/20 px-6 py-3.5 text-[12px] uppercase tracking-[0.18em] text-white/80 transition-all duration-500 hover:border-gold hover:text-gold"
                  >
                    <Icon size={15} />
                    {s.label}
                  </a>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
